import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { Target, Plus, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, DollarSign, RefreshCw, Trash2, Bell, BellRing, Zap } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const STATUS_STYLES = {
  under: { bg: 'bg-success/10 border-success/30', text: 'text-success', label: 'Under Budget', icon: CheckCircle },
  on_track: { bg: 'bg-info/10 border-info/30', text: 'text-info', label: 'On Track', icon: Target },
  over: { bg: 'bg-error/10 border-error/30', text: 'text-error', label: 'Over Budget', icon: AlertTriangle },
  unbudgeted: { bg: 'bg-warning/10 border-warning/30', text: 'text-warning', label: 'Unbudgeted', icon: AlertTriangle },
};

function fmt(v) { return (v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }

export default function BudgetVsActualPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [showCreate, setShowCreate] = useState(false);
  const [newBudget, setNewBudget] = useState({ name: '', fiscal_year: year, line_items: [] });
  const [newItem, setNewItem] = useState({ category: '', amount: '' });
  const [alertRules, setAlertRules] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [showAlertSetup, setShowAlertSetup] = useState(false);
  const [newRule, setNewRule] = useState({ category: '', threshold_warning: 75, threshold_critical: 90, threshold_exceeded: 100 });
  const [schedule, setSchedule] = useState({ enabled: true, interval_hours: 1, next_run: null });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bvaRes, budRes, rulesRes, alertsRes, schedRes] = await Promise.all([
        api.get(`/accounting/budget-vs-actual?fiscal_year=${year}`),
        api.get('/accounting/budgets'),
        api.get('/accounting/budget-alerts/rules').catch(() => ({ data: [] })),
        api.get('/accounting/budget-alerts').catch(() => ({ data: [] })),
        api.get('/budget-alert-schedule').catch(() => ({ data: { enabled: true, interval_hours: 1 } })),
      ]);
      setData(bvaRes.data);
      setBudgets(budRes.data);
      setAlertRules(rulesRes.data || []);
      setActiveAlerts((alertsRes.data || []).filter(a => !a.dismissed));
      setSchedule(schedRes.data || { enabled: true, interval_hours: 1 });
    } catch { toast.error('Failed to load budget data'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [year]);

  const addLineItem = () => {
    if (!newItem.category || !newItem.amount) return;
    setNewBudget(prev => ({
      ...prev,
      line_items: [...prev.line_items, { category: newItem.category, amount: parseFloat(newItem.amount) }],
    }));
    setNewItem({ category: '', amount: '' });
  };

  const removeLineItem = (idx) => {
    setNewBudget(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  };

  const saveBudget = async () => {
    if (!newBudget.name || newBudget.line_items.length === 0) {
      toast.error('Please provide a name and at least one line item');
      return;
    }
    try {
      await api.post('/accounting/budgets', { ...newBudget, fiscal_year: year });
      toast.success('Budget created');
      setShowCreate(false);
      setNewBudget({ name: '', fiscal_year: year, line_items: [] });
      fetchData();
    } catch { toast.error('Failed to create budget'); }
  };

  const deleteBudget = async (id) => {
    try {
      await api.delete(`/accounting/budgets/${id}`);
      toast.success('Budget deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const summary = data?.summary || {};
  const chartData = (data?.comparisons || []).map(c => ({
    name: c.category?.length > 15 ? c.category.substring(0, 15) + '...' : c.category,
    Budgeted: c.budgeted,
    Actual: c.actual,
  }));

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="budget-vs-actual-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-200/50">
              <Target size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Budget vs Actual</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Compare planned budgets against actual spending</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={year} onChange={e => setYear(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="year-selector">
              {[...Array(5)].map((_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
            </select>
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="create-budget-btn"><Plus size={14} className="mr-1" /> New Budget</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create Budget for {year}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <Input placeholder="Budget Name (e.g. Q1 Operating Budget)" value={newBudget.name} onChange={e => setNewBudget(p => ({ ...p, name: e.target.value }))} data-testid="budget-name-input" />
                  <div className="flex gap-2">
                    <Input placeholder="Category" value={newItem.category} onChange={e => setNewItem(p => ({ ...p, category: e.target.value }))} data-testid="line-category-input" />
                    <Input type="number" placeholder="Amount" value={newItem.amount} onChange={e => setNewItem(p => ({ ...p, amount: e.target.value }))} data-testid="line-amount-input" />
                    <Button size="sm" onClick={addLineItem} data-testid="add-line-btn"><Plus size={14} /></Button>
                  </div>
                  {newBudget.line_items.length > 0 && (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {newBudget.line_items.map((item, i) => (
                        <div key={i} className="flex justify-between items-center text-sm bg-muted/50 px-3 py-2 rounded-lg">
                          <span>{item.category}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{fmt(item.amount)}</span>
                            <button onClick={() => removeLineItem(i)} className="text-red-400 hover:text-error"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-right text-muted-foreground pt-1">Total: {fmt(newBudget.line_items.reduce((s, i) => s + i.amount, 0))}</p>
                    </div>
                  )}
                  <Button onClick={saveBudget} className="w-full" data-testid="save-budget-btn">Save Budget</Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw size={14} /></Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[50vh]"><div className="w-8 h-8 border-4 border-info border-t-transparent rounded-full animate-spin" /></div>
        ) : data && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-info/30 bg-info/10"><CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground uppercase">Total Budgeted</p>
                <p className="text-2xl font-bold text-info" data-testid="total-budgeted">{fmt(summary.total_budgeted)}</p>
              </CardContent></Card>
              <Card className="border-success/30 bg-success/10"><CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground uppercase">Total Actual</p>
                <p className="text-2xl font-bold text-success" data-testid="total-actual">{fmt(summary.total_actual)}</p>
              </CardContent></Card>
              <Card className={`${summary.total_variance >= 0 ? 'border-success/30 bg-success/10' : 'border-error/30 bg-error/10'}`}><CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground uppercase">Variance</p>
                <p className={`text-2xl font-bold ${summary.total_variance >= 0 ? 'text-success' : 'text-error'}`} data-testid="variance">{summary.total_variance >= 0 ? '+' : ''}{fmt(summary.total_variance)}</p>
              </CardContent></Card>
              <Card className="border-border"><CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground uppercase">Budget Used</p>
                <p className="text-2xl font-bold text-foreground" data-testid="budget-pct">{summary.overall_pct || 0}%</p>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${summary.overall_pct > 100 ? 'bg-error' : summary.overall_pct > 90 ? 'bg-warning' : 'bg-success'}`} style={{ width: `${Math.min(100, summary.overall_pct || 0)}%` }} />
                </div>
              </CardContent></Card>
            </div>

            {/* Chart */}
            {chartData.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Budget vs Actual by Category</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#a8a29e" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#a8a29e" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={v => fmt(v)} />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Budgeted" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Actual" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, i) => (
                          <Cell key={i} fill={entry.Actual > entry.Budgeted ? '#ef4444' : '#22c55e'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Comparison Table */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Category Breakdown</CardTitle></CardHeader>
              <CardContent className="p-0">
                {(data.comparisons || []).length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Target size={40} className="mx-auto mb-3 opacity-30" />
                    <p>No budget set for {year}. Create one to start tracking.</p>
                  </div>
                ) : (
                  <table className="w-full text-sm" data-testid="comparison-table">
                    <thead className="bg-muted/50"><tr>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Category</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Budgeted</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actual</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Variance</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">% Used</th>
                      <th className="text-center px-4 py-3 font-medium text-muted-foreground">Status</th>
                    </tr></thead>
                    <tbody>
                      {data.comparisons.map((c, i) => {
                        const s = STATUS_STYLES[c.status] || STATUS_STYLES.under;
                        const Icon = s.icon;
                        return (
                          <tr key={i} className="border-t border-border hover:bg-muted/50">
                            <td className="px-4 py-3 font-medium text-foreground">{c.category}</td>
                            <td className="px-4 py-3 text-right text-info font-medium">{fmt(c.budgeted)}</td>
                            <td className="px-4 py-3 text-right text-foreground font-medium">{fmt(c.actual)}</td>
                            <td className={`px-4 py-3 text-right font-bold ${c.variance >= 0 ? 'text-success' : 'text-error'}`}>{c.variance >= 0 ? '+' : ''}{fmt(c.variance)}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground">{c.variance_pct}%</td>
                            <td className="px-4 py-3 text-center"><Badge className={`text-[10px] ${s.bg} ${s.text} border`}><Icon size={10} className="mr-1" />{s.label}</Badge></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Existing Budgets */}
            {budgets.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Saved Budgets</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {budgets.map(b => (
                    <div key={b.id} className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-3">
                      <div>
                        <p className="font-medium text-foreground text-sm">{b.name}</p>
                        <p className="text-xs text-muted-foreground">FY {b.fiscal_year} &middot; {b.line_items?.length || 0} categories &middot; Total: {fmt(b.total_budget)}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteBudget(b.id)} className="text-red-400 hover:text-error"><Trash2 size={14} /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Budget Alert Rules */}
            <Card data-testid="budget-alerts-section">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <BellRing size={16} className="text-warning" /> Budget Alert Rules
                    {activeAlerts.length > 0 && <Badge className="bg-error/20 text-error border-error/30 text-[10px]">{activeAlerts.length} active</Badge>}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={async () => {
                      try {
                        const res = await api.post('/accounting/budget-alerts/check');
                        toast.success(`Budget check complete: ${res.data.alerts_generated} new alerts`);
                        fetchData();
                      } catch { toast.error('Failed to check budgets'); }
                    }} data-testid="check-budgets-btn">
                      <Zap size={14} className="mr-1" /> Run Check
                    </Button>
                    <Dialog open={showAlertSetup} onOpenChange={setShowAlertSetup}>
                      <DialogTrigger asChild>
                        <Button size="sm" data-testid="add-alert-rule-btn"><Plus size={14} className="mr-1" /> Add Rule</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader><DialogTitle>New Budget Alert Rule</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
                            <Input
                              placeholder="e.g. Rent, Utilities, Marketing"
                              value={newRule.category}
                              onChange={e => setNewRule(p => ({ ...p, category: e.target.value }))}
                              data-testid="alert-category-input"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="text-xs font-medium text-warning mb-1 block">Warning %</label>
                              <Input type="number" value={newRule.threshold_warning} onChange={e => setNewRule(p => ({ ...p, threshold_warning: parseInt(e.target.value) || 75 }))} data-testid="alert-warning-input" />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-warning mb-1 block">Critical %</label>
                              <Input type="number" value={newRule.threshold_critical} onChange={e => setNewRule(p => ({ ...p, threshold_critical: parseInt(e.target.value) || 90 }))} data-testid="alert-critical-input" />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-error mb-1 block">Exceeded %</label>
                              <Input type="number" value={newRule.threshold_exceeded} onChange={e => setNewRule(p => ({ ...p, threshold_exceeded: parseInt(e.target.value) || 100 }))} data-testid="alert-exceeded-input" />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">Notifications will be sent to admins when spending crosses these thresholds.</p>
                          <Button className="w-full" onClick={async () => {
                            if (!newRule.category) { toast.error('Category is required'); return; }
                            try {
                              await api.post('/accounting/budget-alerts/rules', newRule);
                              toast.success('Alert rule created');
                              setShowAlertSetup(false);
                              setNewRule({ category: '', threshold_warning: 75, threshold_critical: 90, threshold_exceeded: 100 });
                              fetchData();
                            } catch { toast.error('Failed to create rule'); }
                          }} data-testid="save-alert-rule-btn">Save Rule</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {alertRules.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground text-sm">
                    <Bell size={28} className="mx-auto mb-2 opacity-30" />
                    <p>No alert rules configured</p>
                    <p className="text-xs mt-1">Add rules to get notified when spending approaches budget limits</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {alertRules.map(rule => (
                      <div key={rule.id} className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-3" data-testid={`alert-rule-${rule.id}`}>
                        <div className="flex items-center gap-3">
                          <BellRing size={16} className="text-warning" />
                          <div>
                            <p className="font-medium text-foreground text-sm">{rule.category}</p>
                            <div className="flex gap-2 mt-1">
                              <Badge className="bg-warning/20 text-warning border-warning/30 text-[10px]">{rule.threshold_warning}% warn</Badge>
                              <Badge className="bg-warning/20 text-warning border-warning/30 text-[10px]">{rule.threshold_critical}% critical</Badge>
                              <Badge className="bg-error/20 text-error border-error/30 text-[10px]">{rule.threshold_exceeded}% exceeded</Badge>
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={async () => {
                          try {
                            await api.delete(`/accounting/budget-alerts/rules/${rule.id}`);
                            toast.success('Rule deleted');
                            fetchData();
                          } catch { toast.error('Failed to delete rule'); }
                        }} className="text-red-400 hover:text-error"><Trash2 size={14} /></Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Active Alerts */}
                {activeAlerts.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Active Alerts</p>
                    <div className="space-y-2">
                      {activeAlerts.map(alert => (
                        <div key={alert.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                          alert.severity === 'exceeded' ? 'bg-error/10 border-error/30' :
                          alert.severity === 'critical' ? 'bg-warning/10 border-warning/30' :
                          'bg-warning/10 border-warning/30'
                        }`} data-testid={`active-alert-${alert.id}`}>
                          <div className="flex items-center gap-2">
                            <AlertTriangle size={14} className={
                              alert.severity === 'exceeded' ? 'text-error' :
                              alert.severity === 'critical' ? 'text-warning' : 'text-warning'
                            } />
                            <div>
                              <p className="font-medium text-sm">{alert.category}</p>
                              <p className="text-xs text-muted-foreground">{fmt(alert.actual)} / {fmt(alert.budgeted)} ({alert.percentage}%)</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-[10px] uppercase ${
                              alert.severity === 'exceeded' ? 'bg-error/20 text-error border-error/30' :
                              alert.severity === 'critical' ? 'bg-warning/20 text-warning border-warning/30' :
                              'bg-warning/20 text-warning border-warning/30'
                            }`}>{alert.severity}</Badge>
                            <Button variant="ghost" size="sm" className="text-xs" onClick={async () => {
                              try {
                                await api.put(`/accounting/budget-alerts/${alert.id}/dismiss`);
                                toast.success('Alert dismissed');
                                fetchData();
                              } catch { toast.error('Failed to dismiss'); }
                            }}>Dismiss</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Schedule Config */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Auto-Check Schedule</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {schedule.enabled ? `Checks every ${schedule.interval_hours}h` : 'Disabled'}
                        {schedule.next_run && ` · Next: ${new Date(schedule.next_run).toLocaleTimeString()}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={String(schedule.interval_hours)}
                        onValueChange={async (v) => {
                          try {
                            await api.put('/budget-alert-schedule', { enabled: true, interval_hours: parseInt(v) });
                            setSchedule(p => ({ ...p, interval_hours: parseInt(v), enabled: true }));
                            toast.success(`Schedule updated to every ${v} hour(s)`);
                          } catch { toast.error('Failed to update schedule'); }
                        }}
                      >
                        <SelectTrigger className="w-28 h-8 text-xs" data-testid="schedule-interval-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Every 1h</SelectItem>
                          <SelectItem value="2">Every 2h</SelectItem>
                          <SelectItem value="4">Every 4h</SelectItem>
                          <SelectItem value="8">Every 8h</SelectItem>
                          <SelectItem value="12">Every 12h</SelectItem>
                          <SelectItem value="24">Every 24h</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant={schedule.enabled ? 'outline' : 'default'}
                        className="text-xs h-8"
                        onClick={async () => {
                          try {
                            const newEnabled = !schedule.enabled;
                            await api.put('/budget-alert-schedule', { enabled: newEnabled, interval_hours: schedule.interval_hours });
                            setSchedule(p => ({ ...p, enabled: newEnabled }));
                            toast.success(newEnabled ? 'Auto-check enabled' : 'Auto-check disabled');
                          } catch { toast.error('Failed to update'); }
                        }}
                        data-testid="schedule-toggle-btn"
                      >
                        {schedule.enabled ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </div>
                </div>

              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
